import { Box, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../components/Header";
import { useState, useEffect } from "react";
import ModalUserData from "../../modal/user/modalUserData";
import { getLiquidationView } from "../../services/marcaciones.services";
import InternalResignationTable from "./tables/InternalResignationTable";
import IPSResignationTable from "./tables/IPSResignationTable";
import UnjustifiedDismissalInternalTable from "./tables/UnjustifiedDismissalInternalTable";
import UnjustifiedDismissalIPSTable from "./tables/UnjustifiedDismissalIPSTable";
import InternalSeveranceTable from "./tables/InternalSeveranceTable";
import IPSSeveranceTable from "./tables/IPSSeveranceTable";
import JustCauseIPSTable from "./tables/JustCauseIPSTable";
import JustCauseInternalTable from "./tables/JustCauseInternalTable";
import TrialPeriodTerminationTable from "./tables/TrialTerminationTable";
import JustifiedWithdrawalInternalTable from "./tables/JustifiedWithdrawalInternalTable";
import JustifiedWithdrawalIPSTable from "./tables/JustifiedWithdrawalIPSTable";

const typeOptions = [
  { value: "internal", label: "INTERNO" },
  { value: "ips", label: "IPS" },
];
const causeOptions = [
  { value: "resignation", label: "Renuncia" },
  { value: "unjustified_dismissal", label: "Despido injustificado" },
  { value: "severance", label: "Indemnización" },
  { value: "just_cause", label: "Despido con Justa Causa" },
  { value: "trial_period_termination", label: "Terminación de período de prueba" },
  { value: "justified_withdrawal", label: "Retiro justificado (Art. 84)" },
];

const Liquidacion = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fetchedData, setFetchedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState({
    id_usuario: "",
    type: "",
    cause: "",
  });

  const initialValues = {
    id_usuario: "",
    type: "",
    cause: "",
  };

  const selectionSchema = yup.object().shape({
    id_usuario: yup.string().required("Usuario es requerido"),
    type: yup.string().required("Tipo es requerido"),
    cause: yup.string().required("Motivo es requerido"),
  });

  // Fetch ALL settlement data from backend, then filter by selected user
  useEffect(() => {
    if (!formValues.id_usuario) {
      setFetchedData(null);
      return;
    }
    setLoading(true);
    getLiquidationView()
      .then(data => {
        setFetchedData(data);
        setLoading(false);
      })
      .catch(() => {
        setFetchedData(null);
        setLoading(false);
      });
  }, [formValues.id_usuario]);

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "";
    return Number(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <Box m="20px">
      <Header title="Liquidación" subtitle="Seleccione los parámetros de liquidación" />
      <Formik
        initialValues={initialValues}
        validationSchema={selectionSchema}
        onSubmit={() => {}}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          setFieldValue,
        }) => {
          // Keep formValues updated for useEffect
          if (
            values.id_usuario !== formValues.id_usuario ||
            values.type !== formValues.type ||
            values.cause !== formValues.cause
          ) {
            setFormValues({ ...values });
          }

          // Filter data for selected user
          const selectedUserData = Array.isArray(fetchedData)
            ? fetchedData.find(
                row => String(row.user_id) === String(values.id_usuario)
              )
            : null;
          return (
            <>
              <form>
                <Box display="flex" gap={2} mb="30px">
                  <TextField
                    variant="filled"
                    label="Usuario"
                    name="id_usuario"
                    value={values.id_usuario}
                    onClick={() => setIsModalOpen(true)}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={!!touched.id_usuario && !!errors.id_usuario}
                    helperText={touched.id_usuario && errors.id_usuario}
                    sx={{ flex: 1, cursor: "pointer" }}
                    InputProps={{ readOnly: true }}
                  />
                  <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)}>
                    <DialogTitle>Seleccionar Usuario</DialogTitle>
                    <DialogContent>
                      <ModalUserData
                        open={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        onSelectUsuario={(usuario) => {
                          setFieldValue("id_usuario", usuario.id);
                          setIsModalOpen(false);
                        }}
                      />
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setIsModalOpen(false)}>Cerrar</Button>
                    </DialogActions>
                  </Dialog>

                  <TextField
                    select
                    variant="filled"
                    label="Tipo de Liquidación"
                    name="type"
                    value={values.type}
                    onChange={e => {
                      handleChange(e);
                      setFieldValue("type", e.target.value);
                      setFieldValue("cause", "");
                    }}
                    error={!!touched.type && !!errors.type}
                    helperText={touched.type && errors.type}
                    sx={{ flex: 1 }}
                  >
                    <MenuItem value="">Tipo</MenuItem>
                    {typeOptions.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    variant="filled"
                    label="Motivo"
                    name="cause"
                    value={values.cause}
                    onChange={handleChange}
                    error={!!touched.cause && !!errors.cause}
                    helperText={touched.cause && errors.cause}
                    sx={{ flex: 1 }}
                    disabled={!values.type}
                  >
                    <MenuItem value="">Motivo</MenuItem>
                    {causeOptions.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* Internal Resignation: Filter and display only selected user data */}
                {(values.type === "internal" && values.cause === "resignation" && values.id_usuario) && (
                  loading ? (
                    <Box color="white">Cargando datos...</Box>
                  ) : selectedUserData ? (
                    <InternalResignationTable
                      salary={formatNumber(selectedUserData.monthly_salary_real)}
                      christmasBonus={formatNumber(selectedUserData.christmas_bonus)}
                      overtime={formatNumber(selectedUserData.overtime)}
                      nightBonus={formatNumber(selectedUserData.night_bonus)}
                      accruedVacation={formatNumber(selectedUserData.accrued_vacationsreal
                      )}
                      deductions={formatNumber(
                        Number(selectedUserData.originalAdelanto || 0) +
                        Number(selectedUserData.bancoDeduction || 0) +
                        Number(selectedUserData.total_descuento || 0))
                      }
                      total={formatNumber(Number(selectedUserData.monthly_salary_real || 0) +
                                          Number(selectedUserData.christmas_bonus || 0) +
                                          Number(selectedUserData.overtime || 0) + 
                                          Number(selectedUserData.night_bonus || 0) +
                                          Number(selectedUserData.accrued_vacationsreal || 0) -
                                          Number(selectedUserData.originalAdelanto || 0) -
                                          Number(selectedUserData.bancoDeduction || 0) -
                                          Number(selectedUserData.total_descuento || 0))}
                    />
                  ) : (
                    <Box color="white">No hay datos para este usuario.</Box>
                  )
                )}

                {/* All other settlement cases remain manual/demo */}
                {(values.type === "ips" && values.cause === "resignation" && values.id_usuario) && (
                  loading ? (
                    <Box color="white">Cargando datos...</Box>
                  ) : selectedUserData ? (
                    <IPSResignationTable
                      declaredSalary={Number(selectedUserData.monthly_salary) || 0}
                      nightBonus={Number(selectedUserData.night_bonus) || 0}
                      advanceDeduction={Number(selectedUserData.bancoDeduction + selectedUserData.adelanto_deduction + selectedUserData.total_descuento) || 0}
                      ipsDeductions={Number(selectedUserData.ips_contribution) || 0}
                      familyBonus={Number(selectedUserData.family_bonus) || 0}
                      total={
                        (Number(selectedUserData.monthly_salary) || 0) +
                        (Number(selectedUserData.family_bonus) || 0) +
                        (Number(selectedUserData.night_bonus) || 0) -
                        (Number(selectedUserData.bancoDeduction + selectedUserData.adelanto_deduction + selectedUserData.total_descuento) || 0) -
                        (Number(selectedUserData.ips_contribution) || 0)
                      }
                      onValuesChange={(updated) => {
                        // Optionally handle updates here
                      }}
                    />
                  ) : (
                    <Box color="white">No hay datos para este usuario.</Box>
                  )
                )}
                {(values.type === "internal" && values.cause === "unjustified_dismissal" && values.id_usuario) && (
                  loading ? (
                    <Box color="white">Cargando datos...</Box>
                  ) : selectedUserData ? (
                    <UnjustifiedDismissalInternalTable
                      salary={Number(selectedUserData.monthly_salary_real) || 0}
                      christmasBonus={Number(selectedUserData.christmas_bonus) || 0}
                      accruedVac={Number(selectedUserData.accrued_vacationsreal) || 0}
                      proportionalVac={Number(selectedUserData.proportional_vacationsreal) || 0}
                      notice={Number(selectedUserData.notice) || 0}
                      overtime={Number(selectedUserData.overtime) || 0}
                      advanceDeduction={Number(selectedUserData.originalAdelanto) || 0}
                      otherDeductions={Number(selectedUserData.bancoDeduction + selectedUserData.total_descuento) || 0}
                      severance={Number(selectedUserData.monthly_salary_real) || 0}
                      total={
                        (Number(selectedUserData.monthly_salary_real) || 0) +
                        (Number(selectedUserData.christmas_bonus) || 0) +
                        (Number(selectedUserData.accrued_vacationsreal) || 0) +
                        (Number(selectedUserData.proportional_vacationsreal) || 0) +
                        (Number(selectedUserData.notice) || 0) +
                        (Number(selectedUserData.overtime) || 0) -
                        (Number(selectedUserData.originalAdelanto) || 0) -
                        (Number(selectedUserData.bancoDeduction + selectedUserData.total_descuento) || 0) +
                        (Number(selectedUserData.internal_severance) || 0)
                      }
                    />
                  ) : (
                    <Box color="white">No hay datos para este usuario.</Box>
                  )
                )}
                {values.type === "ips" && values.cause === "unjustified_dismissal" && values.id_usuario && (
                  loading ? (
                    <Box color="white">Cargando datos...</Box>
                  ) : selectedUserData ? (
                    <UnjustifiedDismissalIPSTable
                      declaredSalary={Number(selectedUserData.monthly_salary) || 0}
                      nightBonus={Number(selectedUserData.night_bonus) || 0}
                      accruedVac={Number(selectedUserData.accrued_vacations) || 0}
                      proportionalVac={Number(selectedUserData.proportional_vacations) || 0}
                      notice={Number(selectedUserData.notice_ips) || 0}
                      advanceDeduction={Number(selectedUserData.originalAdelanto) || 0}
                      otherDeductions={Number(selectedUserData.bancoDeduction + selectedUserData.total_descuento) || 0}
                      ipsSeverance={Number(selectedUserData.monthly_salary) || 0}
                      total={
                        (Number(selectedUserData.monthly_salary) || 0) +
                        (Number(selectedUserData.night_bonus) || 0) +
                        (Number(selectedUserData.accrued_vacations) || 0) +
                        (Number(selectedUserData.proportional_vacations) || 0) +
                        (Number(selectedUserData.notice_ips) || 0) -
                        (Number(selectedUserData.originalAdelanto) || 0) -
                        (Number(selectedUserData.bancoDeduction + selectedUserData.total_descuento) || 0) +
                        (Number(selectedUserData.ips_severance) || 0)
                      }
                    />
                  ) : (
                    <Box color="white">No hay datos para este usuario.</Box>
                  )
                )}
                {(values.type === "internal" && values.cause === "severance" && values.id_usuario) && (
                  loading ? (
                    <Box color="white">Cargando datos...</Box>
                  ) : selectedUserData ? (
                    <InternalSeveranceTable
                      internalSeverance={Number(selectedUserData.monthly_salary_real) || 0}
                      onValuesChange={(newValue) => {
                        // Optionally handle the changed value here
                      }}
                    />
                  ) : (
                    <Box color="white">No hay datos para este usuario.</Box>
                  )
                )}
                {(values.type === "ips" && values.cause === "severance" && values.id_usuario) && (
                  loading ? (
                    <Box color="white">Cargando datos...</Box>
                  ) : selectedUserData ? (
                    <IPSSeveranceTable
                      ipsSeverance={Number(selectedUserData.monthly_salary) || 0}
                      onValuesChange={(newValue) => {
                        // Handle updated ipsSeverance value here if needed
                      }}
                    />
                  ) : (
                    <Box color="white">No hay datos para este usuario.</Box>
                  )
                )}
                {(values.type === "internal" && values.cause === "just_cause" && values.id_usuario) && (
                  loading ? (
                    <Box color="white">Cargando datos...</Box>
                  ) : selectedUserData ? (
                    <JustCauseInternalTable
                      salary={Number(selectedUserData.monthly_salary_real) || 0}
                      christmasBonus={Number(selectedUserData.christmas_bonus) || 0}
                      accruedVacation={Number(selectedUserData.accrued_vacationsreal) || 0}
                      total={
                        (Number(selectedUserData.monthly_salary_real) || 0) +
                        (Number(selectedUserData.christmas_bonus) || 0) + 
                        (Number(selectedUserData.accrued_vacationsreal) || 0)
                      }
                    />
                  ) : (
                    <Box color="white">No hay datos para este usuario.</Box>
                  )
                )}
                {(values.type === "ips" && values.cause === "just_cause" && values.id_usuario) && (
                  loading ? (
                    <Box color="white">Cargando datos...</Box>
                  ) : selectedUserData ? (
                    <JustCauseIPSTable
                      declaredSalary={Number(selectedUserData.monthly_salary) || 0}
                      christmasBonus={Number(selectedUserData.christmas_bonus_ips) || 0}
                      accruedVacation={Number(selectedUserData.accrued_vacations) || 0}
                      total={
                        (Number(selectedUserData.monthly_salary) || 0) +
                        (Number(selectedUserData.christmas_bonus_ips) || 0) +
                        (Number(selectedUserData.accrued_vacations) || 0)
                      }
                    />
                  ) : (
                    <Box color="white">No hay datos para este usuario.</Box>
                  )
                )}
                {(values.type === "ips" && values.cause === "trial_period_termination" && values.id_usuario) && (
                  loading ? (
                    <Box color="white">Cargando datos...</Box>
                  ) : selectedUserData ? (
                    <TrialPeriodTerminationTable
                      proportionalSalary={Number(selectedUserData.monthly_salary) || 0}
                      christmasBonus={Number(selectedUserData.christmas_bonus_ips) || 0}
                      total={
                        (Number(selectedUserData.monthly_salary) || 0) +
                        (Number(selectedUserData.christmas_bonus_ips) || 0)
                      }
                      onValuesChange={({ proportionalSalary, christmasBonus }) => {
                        // Optional: handle updates if needed in parent component
                      }}
                    />
                  ) : (
                    <Box color="white">No hay datos para este usuario.</Box>
                  )
                )}
                {(values.type === "internal" && values.cause === "trial_period_termination" && values.id_usuario) && (
                  loading ? (
                    <Box color="white">Cargando datos...</Box>
                  ) : selectedUserData ? (
                    <TrialPeriodTerminationTable
                      proportionalSalary={Number(selectedUserData.monthly_salary_real) || 0}
                      christmasBonus={Number(selectedUserData.christmas_bonus) || 0}
                      total={
                        (Number(selectedUserData.monthly_salary_real) || 0) +
                        (Number(selectedUserData.christmas_bonus) || 0)
                      }
                      onValuesChange={({ proportionalSalary, christmasBonus }) => {
                        // Optional: handle updates if needed in parent component
                      }}
                    />
                  ) : (
                    <Box color="white">No hay datos para este usuario.</Box>
                  )
                )}
                {(values.type === "internal" && values.cause === "justified_withdrawal" && values.id_usuario) && (
                  loading ? (
                    <Box color="white">Cargando datos...</Box>
                  ) : selectedUserData ? (
                    <JustifiedWithdrawalInternalTable
                      salary={Number(selectedUserData.monthly_salary_real) || 0}
                      christmasBonus={Number(selectedUserData.christmas_bonus) || 0}
                      accruedVacation={Number(selectedUserData.accrued_vacationsreal) || 0}
                      proportionalVacation={Number(selectedUserData.proportional_vacationsreal) || 0}
                      notice={Number(selectedUserData.notice) || 0}
                      internalSeverance={Number(selectedUserData.monthly_salary_real) || 0}
                      total={
                        (Number(selectedUserData.monthly_salary_real) || 0) +
                        (Number(selectedUserData.christmas_bonus) || 0) +
                        (Number(selectedUserData.accrued_vacationsreal) || 0) +
                        (Number(selectedUserData.proportional_vacationsreal) || 0) +
                        (Number(selectedUserData.notice) || 0) +
                        (Number(selectedUserData.internal_severance) || 0)
                      }
                      onValuesChange={(values) => {
                        // Optional: update parent state here
                      }}
                    />
                  ) : (
                    <Box color="white">No hay datos para este usuario.</Box>
                  )
                )}
                {(values.type === "ips" && values.cause === "justified_withdrawal" && values.id_usuario) && (
                loading ? (
                  <Box color="white">Cargando datos...</Box>
                ) : selectedUserData ? (
                  <JustifiedWithdrawalIPSTable
                    declaredSalary={Number(selectedUserData.monthly_salary) || 0}
                    christmasBonus={Number(selectedUserData.christmas_bonus_ips) || 0}
                    accruedVacation={Number(selectedUserData.accrued_vacations) || 0}
                    proportionalVacation={Number(selectedUserData.proportional_vacations) || 0}
                    notice={Number(selectedUserData.notice_ips ?? 0)}
                    ipsSeverance={Number(selectedUserData.monthly_salary) || 0}
                    total={
                      (Number(selectedUserData.monthly_salary) || 0) +
                      (Number(selectedUserData.christmas_bonus_ips) || 0) +
                      (Number(selectedUserData.accrued_vacations) || 0) +
                      (Number(selectedUserData.proportional_vacations) || 0) +
                      (Number(selectedUserData.notice_ips) || 0) +
                      (Number(selectedUserData.ips_severance) || 0)
                    }
                    onValuesChange={(values) => {
                      // Optional: you can handle updated values here if you want
                    }}
                  />
                ) : (
                  <Box color="white">No hay datos para este usuario.</Box>
                )
              )}
              </form>
            </>
          );
        }}
      </Formik>
    </Box>
  );
};

export default Liquidacion;
